import { message } from 'antd';
import { useState } from 'react';
import { ApiError, getApiValidationMessage, useFileFetcher } from '@/services/queryUtils';
import { downloadFile } from '@/utils/dataDownloadUtils';

interface UseDownloadExportParams {
  /** null means there is nothing to download. */
  path: string | null;
  onSuccess: () => void;
  /** Receives the HTTP status for API errors, otherwise `'UNKNOWN_ERROR'`. */
  onFailure: (errorCode: string) => void;
}

/**
 * Fetches an export file and saves it, shared by the download drawers. Shows the API validation
 * message for a text 400, and a generic message for any other failure. Mixpanel tracking stays
 * with the caller because each drawer has its own typed events.
 */
const useDownloadExport = ({
  path,
  onSuccess,
  onFailure,
}: UseDownloadExportParams): { isDownloading: boolean; download: () => Promise<void> } => {
  const fetchFile = useFileFetcher();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = async () => {
    if (!path) {
      message.error('No data to download');
      return;
    }

    setIsDownloading(true);
    try {
      const { blob, fileName } = await fetchFile(path);
      downloadFile(blob, fileName);
      onSuccess();
    } catch (error) {
      onFailure(error instanceof ApiError ? String(error.response.status) : 'UNKNOWN_ERROR');
      console.error('Error downloading export', error);
      message.error((await getApiValidationMessage(error)) ?? 'Failed to download data');
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, download };
};

export default useDownloadExport;
